import React from 'react';
import { Tags, Globe, Landmark, Building2, CreditCard } from 'lucide-react';
import type { TableColumn } from '../components/AdminTable';

export interface MasterDataFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select' | 'boolean';
  required?: boolean;
  placeholder?: string;
  /** For type 'select'. */
  options?: { value: string; label: string }[];
  /** Initial value when creating a new record (e.g. 'true' for a boolean that defaults on). */
  defaultValue?: string;
  /** Return an error message, or null if the value is valid. */
  validate?: (value: string) => string | null;
}

export interface MasterDataEntityConfig<T = Record<string, unknown>> {
  slug: string;
  label: string;
  navLabel: string;
  icon: React.ReactNode;
  description: string;
  columns: TableColumn<T>[];
  fields: MasterDataFieldConfig[];
  rowKey: (row: T) => string;
  supportsCreate: boolean;
  supportsDelete: boolean;
  supportsBulkUpload: boolean;
}

export interface SkillRow {
  id: string;
  name: string;
  category: string | null;
  verificationValidityMonths: number | null;
  userCount: number;
}

const skillsConfig: MasterDataEntityConfig<SkillRow> = {
  slug: 'skills',
  label: 'Skills',
  navLabel: 'Skills',
  icon: <Tags size={16} />,
  description: 'The skill catalog users self-declare, get assessed on, and endorse each other for.',
  columns: [
    { key: 'name', header: 'Name', render: (s) => s.name },
    {
      key: 'category',
      header: 'Category',
      render: (s) => s.category ?? <span style={{ color: 'var(--adm-muted)' }}>—</span>,
    },
    {
      key: 'validity',
      header: 'Verification validity',
      render: (s) =>
        s.verificationValidityMonths ? `${s.verificationValidityMonths} mo` : 'Never expires',
    },
    { key: 'userCount', header: 'Users', width: 90, render: (s) => s.userCount },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'category', label: 'Category', type: 'text' },
    {
      key: 'verificationValidityMonths',
      label: 'Verification validity (months)',
      type: 'number',
      placeholder: 'Leave blank to never expire',
      validate: (v) =>
        v && (!/^\d+$/.test(v) || Number(v) < 1) ? 'Must be a positive number' : null,
    },
  ],
  rowKey: (s) => s.id,
  supportsCreate: true,
  supportsDelete: true,
  supportsBulkUpload: false,
};

export interface CountryRow {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  phoneCode: string | null;
  flagEmoji: string | null;
  region: string | null;
  subregion: string | null;
}

const countriesConfig: MasterDataEntityConfig<CountryRow> = {
  slug: 'countries',
  label: 'Countries',
  navLabel: 'Countries',
  icon: <Globe size={16} />,
  description: 'Country reference data — states and cities are scoped to these.',
  columns: [
    { key: 'name', header: 'Name', render: (c) => c.name },
    { key: 'iso2', header: 'ISO2', width: 70, render: (c) => c.iso2 },
    { key: 'iso3', header: 'ISO3', width: 70, render: (c) => c.iso3 },
    {
      key: 'region',
      header: 'Region',
      render: (c) => c.region ?? <span style={{ color: 'var(--adm-muted)' }}>—</span>,
    },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    {
      key: 'iso2',
      label: 'ISO2 code',
      type: 'text',
      required: true,
      validate: (v) => (v && !/^[A-Za-z]{2}$/.test(v) ? 'Must be exactly 2 letters' : null),
    },
    {
      key: 'iso3',
      label: 'ISO3 code',
      type: 'text',
      required: true,
      validate: (v) => (v && !/^[A-Za-z]{3}$/.test(v) ? 'Must be exactly 3 letters' : null),
    },
    { key: 'phoneCode', label: 'Phone code', type: 'text', placeholder: '+91' },
    { key: 'flagEmoji', label: 'Flag emoji', type: 'text' },
    { key: 'region', label: 'Region', type: 'text' },
    { key: 'subregion', label: 'Subregion', type: 'text' },
  ],
  rowKey: (c) => String(c.id),
  supportsCreate: true,
  supportsDelete: true,
  supportsBulkUpload: false,
};

export interface StateRow {
  id: number;
  name: string;
  stateCode: string | null;
  countryId: number;
}

const statesConfig: MasterDataEntityConfig<StateRow> = {
  slug: 'states',
  label: 'States',
  navLabel: 'States',
  icon: <Landmark size={16} />,
  description: 'State/province reference data, scoped to a country. Cities are scoped to these.',
  columns: [
    { key: 'name', header: 'Name', render: (s) => s.name },
    { key: 'countryId', header: 'Country ID', width: 100, render: (s) => s.countryId },
    {
      key: 'stateCode',
      header: 'Code',
      width: 80,
      render: (s) => s.stateCode ?? <span style={{ color: 'var(--adm-muted)' }}>—</span>,
    },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    {
      key: 'countryId',
      label: 'Country ID',
      type: 'number',
      required: true,
      placeholder: 'See the Countries list for its numeric id',
    },
    { key: 'stateCode', label: 'State code', type: 'text' },
  ],
  rowKey: (s) => String(s.id),
  supportsCreate: true,
  supportsDelete: true,
  supportsBulkUpload: false,
};

export interface CityRow {
  id: number;
  name: string;
  countryId: number;
  stateId: number | null;
  lat: number | string | null;
  lng: number | string | null;
  timezone: string | null;
}

const citiesConfig: MasterDataEntityConfig<CityRow> = {
  slug: 'cities',
  label: 'Cities',
  navLabel: 'Cities',
  icon: <Building2 size={16} />,
  description: 'City reference data used for profile locations and typeahead search.',
  columns: [
    { key: 'name', header: 'Name', render: (c) => c.name },
    { key: 'countryId', header: 'Country ID', width: 100, render: (c) => c.countryId },
    {
      key: 'stateId',
      header: 'State ID',
      width: 90,
      render: (c) => c.stateId ?? <span style={{ color: 'var(--adm-muted)' }}>—</span>,
    },
    {
      key: 'timezone',
      header: 'Timezone',
      render: (c) => c.timezone ?? <span style={{ color: 'var(--adm-muted)' }}>—</span>,
    },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    {
      key: 'countryId',
      label: 'Country ID',
      type: 'number',
      required: true,
      placeholder: 'See the Countries list for its numeric id',
    },
    {
      key: 'stateId',
      label: 'State ID',
      type: 'number',
      placeholder: 'See the States list for its numeric id (optional)',
    },
    { key: 'lat', label: 'Latitude', type: 'number' },
    { key: 'lng', label: 'Longitude', type: 'number' },
    { key: 'timezone', label: 'Timezone', type: 'text', placeholder: 'Asia/Kolkata' },
  ],
  rowKey: (c) => String(c.id),
  supportsCreate: true,
  supportsDelete: true,
  supportsBulkUpload: false,
};

export interface SubscriptionPlanRow {
  id: string;
  name: string;
  type: string;
  context: string;
  isActive: boolean;
  priceMonthly: number | null;
  priceYearly: number | null;
  currency: string;
  displayOrder: number;
  userSubscriptionCount: number;
  organizationSubscriptionCount: number;
}

const PLAN_TYPE_OPTIONS = [
  'FREE',
  'PREMIUM',
  'COMPANY_BASIC',
  'COMPANY_PRO',
  'COLLEGE_BASIC',
  'COLLEGE_PRO',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const PLAN_CONTEXT_OPTIONS = ['INDIVIDUAL', 'COMPANY', 'COLLEGE'].map((v) => ({
  value: v,
  label: v,
}));

const subscriptionPlansConfig: MasterDataEntityConfig<SubscriptionPlanRow> = {
  slug: 'subscription-plans',
  label: 'Subscription Plans',
  navLabel: 'Subscription Plans',
  icon: <CreditCard size={16} />,
  description:
    'Plan catalog (name, pricing, status). Feature limits are configured separately from Billing → Entitlements after creating a plan.',
  columns: [
    { key: 'name', header: 'Name', render: (p) => p.name },
    { key: 'type', header: 'Type', render: (p) => p.type },
    { key: 'context', header: 'Context', render: (p) => p.context },
    {
      key: 'price',
      header: 'Price (mo / yr)',
      render: (p) => `${p.priceMonthly ?? '—'} / ${p.priceYearly ?? '—'} ${p.currency}`,
    },
    {
      key: 'isActive',
      header: 'Active',
      width: 80,
      render: (p) => (p.isActive ? '✅' : '—'),
    },
    {
      key: 'usage',
      header: 'Subscribers',
      width: 100,
      render: (p) => p.userSubscriptionCount + p.organizationSubscriptionCount,
    },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'type', label: 'Type', type: 'select', required: true, options: PLAN_TYPE_OPTIONS },
    {
      key: 'context',
      label: 'Context',
      type: 'select',
      required: true,
      options: PLAN_CONTEXT_OPTIONS,
    },
    { key: 'isActive', label: 'Active', type: 'boolean', defaultValue: 'true' },
    { key: 'priceMonthly', label: 'Price (monthly)', type: 'number' },
    { key: 'priceYearly', label: 'Price (yearly)', type: 'number' },
    { key: 'currency', label: 'Currency', type: 'text', placeholder: 'INR', defaultValue: 'INR' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'displayOrder', label: 'Display order', type: 'number', defaultValue: '0' },
    { key: 'trialDays', label: 'Trial days', type: 'number', defaultValue: '0' },
  ],
  rowKey: (p) => p.id,
  supportsCreate: true,
  supportsDelete: true,
  supportsBulkUpload: false,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MASTER_DATA_REGISTRY: MasterDataEntityConfig<any>[] = [
  skillsConfig,
  countriesConfig,
  statesConfig,
  citiesConfig,
  subscriptionPlansConfig,
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getMasterDataConfig(slug: string): MasterDataEntityConfig<any> | undefined {
  return MASTER_DATA_REGISTRY.find((e) => e.slug === slug);
}
